// ── Per-school module entitlements: the query ────────────────────────────────
//
// One react-query entry backed by GET /me/entitlements, shared by everything
// that asks "is this module on for me?". UI code should not read it directly:
// use `useModuleAccess()` (hooks/use-module-access.ts), which turns the
// response into hasModule / moduleState / visibility.
//
// Freshness (owner: "changes must show within seconds"): the query is stale
// after 20 s, and is refetched by `useModuleAccessSync()` on the socket event
// `entitlements:changed`, on window focus / tab visible, on Capacitor app
// resume, and when any API call returns 403 MODULE_NOT_IN_PLAN.
//
// The last good response is remembered in localStorage per user + school so a
// cold start paints the right menu immediately instead of flashing modules
// that are about to disappear. It is only ever a placeholder: the query still
// refetches on mount.

import { QueryClient, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { apiGet } from "@/api/client";
import { useAuthStore } from "@/store/auth-store";
import type { EntitlementsResponse } from "@/lib/module-access";

export type { EntitlementsResponse } from "@/lib/module-access";

export const ENTITLEMENTS_KEY = ["me", "entitlements"] as const;
export const ENTITLEMENTS_STALE_MS = 20_000;

const STORAGE_PREFIX = "ch:entitlements:v1:";

function storageKey(userId: string | undefined, schoolId: string | null): string | null {
  return userId ? `${STORAGE_PREFIX}${userId}:${schoolId ?? "-"}` : null;
}

export function readRememberedEntitlements(userId: string | undefined, schoolId: string | null): EntitlementsResponse | undefined {
  const key = storageKey(userId, schoolId);
  if (!key) return undefined;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as EntitlementsResponse;
    return Array.isArray(parsed?.modules) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export function rememberEntitlements(userId: string | undefined, schoolId: string | null, data: EntitlementsResponse): void {
  const key = storageKey(userId, schoolId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* private mode / quota: the remembered copy is a nicety */
  }
}

/**
 * The provider's client, or a private one when a small piece of UI (a
 * dashboard tile in a preview, a unit test) is rendered outside a provider.
 * `useModuleAccess` sits inside dozens of presentational primitives; none of
 * them should crash because nobody mounted react-query around them. (Signed
 * out / unauthenticated it never fetches, so the private client stays empty.)
 */
let detachedClient: QueryClient | null = null;
export function useAmbientQueryClient(): QueryClient {
  try {
    return useQueryClient();
  } catch {
    detachedClient ??= new QueryClient();
    return detachedClient;
  }
}

export function useEntitlements(): UseQueryResult<EntitlementsResponse> {
  const client = useAmbientQueryClient();
  const isAuthed = useAuthStore((s) => Boolean(s.token));
  const userId = useAuthStore((s) => s.user?.id);
  // (not `realSchoolId`: a few tests mock the store with only `useAuthStore`)
  const schoolId = useAuthStore((s) => (s.activeSchoolId && s.activeSchoolId !== "platform" ? s.activeSchoolId : null));
  return useQuery(
    {
      queryKey: ENTITLEMENTS_KEY,
      queryFn: async () => {
        const data = await apiGet<EntitlementsResponse>("/me/entitlements");
        rememberEntitlements(userId, schoolId, data);
        return data;
      },
      enabled: isAuthed,
      staleTime: ENTITLEMENTS_STALE_MS,
      // initialData with an old updatedAt: painted at once, refetched at once.
      initialData: () => readRememberedEntitlements(userId, schoolId),
      initialDataUpdatedAt: 0,
    },
    client,
  );
}
